import { useState } from 'react';
import { Icons } from './Icons';
import { LogEntry, LogEntryDetails, getLogDetails } from '../services/api';

interface LogEntryRowProps {
  entry: LogEntry;
  isSearchResult?: boolean;
  isHighlighted?: boolean;
  onJumpToContext?: (entry: LogEntry) => void;
}

const levelColors: Record<string, string> = {
  ERROR: 'text-red-400',
  WARN: 'text-yellow-400',
  INFO: 'text-blue-400',
  DEBUG: 'text-gray-400',
  UNKNOWN: 'text-gray-500',
};

const levelBgColors: Record<string, string> = {
  ERROR: 'bg-red-500/10',
  WARN: 'bg-yellow-500/10',
  INFO: 'bg-blue-500/10',
  DEBUG: 'bg-gray-500/10',
  UNKNOWN: 'bg-gray-500/5',
};

function getStatusColor(status: number): string {
  if (status >= 500) return 'text-red-400';
  if (status >= 400) return 'text-yellow-400';
  if (status >= 300) return 'text-blue-400';
  return 'text-green-400';
}

function getStatusBgColor(status: number): string {
  if (status >= 500) return 'bg-red-500/20';
  if (status >= 400) return 'bg-yellow-500/20';
  if (status >= 300) return 'bg-blue-500/20';
  return 'bg-green-500/20';
}

function getResponseTimeColor(ms: number): string {
  if (ms > 5000) return 'text-red-400';
  if (ms > 1000) return 'text-yellow-400';
  return 'text-green-400';
}

function formatResponseTime(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  return `${ms}ms`;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function LogEntryRow({ entry, isSearchResult, isHighlighted, onJumpToContext }: LogEntryRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState<LogEntryDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    if (details) {
      setExpanded(true);
      return;
    }

    if (!entry.id) {
      setError('No log ID available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getLogDetails(entry.id);
      setDetails(result);
      setExpanded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load details');
    } finally {
      setLoading(false);
    }
  };

  const timestamp = new Date(entry.timestamp);
  const timeStr = timestamp.toLocaleTimeString('en-US', { hour12: false });
  const dateStr = timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const hasHttpDetails = details && (
    details.httpMethod || details.urlPath || details.statusCode
  );

  const hasForensicDetails = details && (
    details.clientIp || details.userAgent || details.requestId || details.referer ||
    details.hostHeader || details.serverPort || details.requestBytes || details.responseBytes ||
    details.protocol || details.serverName || details.authenticatedUser
  );

  return (
    <div
      className={`border-b border-[var(--border-subtle)] ${levelBgColors[entry.level] || ''} ${
        isHighlighted ? 'ring-2 ring-yellow-400/50 bg-yellow-500/10' : ''
      }`}
      id={entry.id ? `log-entry-${entry.id}` : undefined}
    >
      {/* Main row */}
      <div className="flex items-center gap-2 px-3 py-1 font-mono text-xs hover:bg-white/5">
        {/* Expand button */}
        <button
          onClick={handleExpand}
          className="flex-shrink-0 p-0.5 rounded hover:bg-white/10 text-gray-500 hover:text-gray-300 transition-colors"
          title={expanded ? 'Collapse details' : 'Show details'}
        >
          {loading ? (
            <Icons.Spinner className="w-3 h-3" />
          ) : (
            <svg
              className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </button>

        {/* Timestamp - compact single line */}
        <span className="flex-shrink-0 text-gray-500 whitespace-nowrap" title={entry.timestamp}>
          <span className="text-gray-600">{dateStr}</span> {timeStr}
        </span>

        {/* Level badge */}
        <span className={`flex-shrink-0 w-[52px] text-center font-semibold ${levelColors[entry.level]}`}>
          {entry.level}
        </span>

        {/* Message */}
        <span className="flex-1 text-gray-200 break-all whitespace-pre-wrap">
          {entry.message}
        </span>

        {/* Jump to context button - only shown for search results */}
        {isSearchResult && onJumpToContext && entry.id && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onJumpToContext(entry);
            }}
            className="flex-shrink-0 px-2 py-0.5 text-[10px] bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded border border-blue-600/30 transition-colors"
            title="Show this entry in the live timeline"
          >
            Show in timeline
          </button>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="ml-6 mr-3 mb-2">
          {error ? (
            <div className="text-red-400 text-xs px-3 py-2">{error}</div>
          ) : details ? (
            <div className="text-xs">
              {/* HTTP Request Card - shown if HTTP details exist */}
              {hasHttpDetails && (
                <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)] overflow-hidden mb-2">
                  {/* Request Header */}
                  <div className="flex items-center gap-3 px-3 py-2 bg-black/20 border-b border-[var(--border-subtle)]">
                    <span className="text-purple-400 font-bold">{details.httpMethod || 'GET'}</span>
                    <span className="text-gray-300 font-mono flex-1 truncate">
                      {details.urlPath}
                      {details.urlQuery && (
                        <span className="text-gray-500">?{details.urlQuery}</span>
                      )}
                    </span>
                    {details.statusCode && (
                      <span className={`px-2 py-0.5 rounded font-bold ${getStatusBgColor(details.statusCode)} ${getStatusColor(details.statusCode)}`}>
                        {details.statusCode}
                      </span>
                    )}
                    {details.responseTimeMs !== undefined && (
                      <span className={`font-mono ${getResponseTimeColor(details.responseTimeMs)}`}>
                        {formatResponseTime(details.responseTimeMs)}
                      </span>
                    )}
                  </div>

                  {/* Request Details Grid */}
                  {hasForensicDetails && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-2">
                      {details.clientIp && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Client IP</span>
                          <span className="text-orange-400 font-mono">{details.clientIp}</span>
                        </div>
                      )}

                      {details.hostHeader && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Host</span>
                          <span className="text-cyan-400 font-mono">{details.hostHeader}</span>
                        </div>
                      )}

                      {details.protocol && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Protocol</span>
                          <span className="text-gray-400">{details.protocol}</span>
                        </div>
                      )}

                      {details.serverPort && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Server Port</span>
                          <span className="text-gray-400">{details.serverPort}</span>
                        </div>
                      )}

                      {(details.requestBytes !== undefined || details.responseBytes !== undefined) && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Size</span>
                          <span className="text-gray-400">
                            {details.requestBytes !== undefined && <span title="Request size">↑{formatBytes(details.requestBytes)}</span>}
                            {details.requestBytes !== undefined && details.responseBytes !== undefined && ' / '}
                            {details.responseBytes !== undefined && <span title="Response size">↓{formatBytes(details.responseBytes)}</span>}
                          </span>
                        </div>
                      )}

                      {details.serverName && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Server</span>
                          <span className="text-gray-400 font-mono text-[10px]">{details.serverName}</span>
                        </div>
                      )}

                      {details.authenticatedUser && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Auth User</span>
                          <span className="text-yellow-400">{details.authenticatedUser}</span>
                        </div>
                      )}

                      {details.requestId && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-24">Request ID</span>
                          <span className="text-gray-400 font-mono text-[10px] truncate" title={details.requestId}>
                            {details.requestId}
                          </span>
                        </div>
                      )}

                      {details.userAgent && (
                        <div className="col-span-2 flex items-start gap-2">
                          <span className="text-gray-500 w-24 flex-shrink-0">User Agent</span>
                          <span className="text-gray-400 text-[10px] break-all">{details.userAgent}</span>
                        </div>
                      )}

                      {details.referer && (
                        <div className="col-span-2 flex items-start gap-2">
                          <span className="text-gray-500 w-24 flex-shrink-0">Referer</span>
                          <span className="text-gray-400 text-[10px] break-all">{details.referer}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Metadata section - shown if no HTTP details or as additional info */}
              {!hasHttpDetails && (
                <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)] px-3 py-2 mb-2">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {details.category && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 w-16">Category</span>
                        <span className="text-cyan-400">{details.category}</span>
                      </div>
                    )}

                    {details.clientIp && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 w-16">Client IP</span>
                        <span className="text-orange-400 font-mono">{details.clientIp}</span>
                      </div>
                    )}

                    {details.requestId && (
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="text-gray-500 w-16">Request ID</span>
                        <span className="text-gray-400 font-mono text-[10px]">{details.requestId}</span>
                      </div>
                    )}

                    {!details.category && !details.clientIp && !details.requestId && (
                      <div className="col-span-2 text-gray-500 italic">
                        No additional metadata available.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Raw log section - collapsible */}
              <details className="group">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-400 text-[10px] uppercase tracking-wide flex items-center gap-1">
                  <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Raw Log
                </summary>
                <pre className="mt-1 p-2 bg-black/30 rounded text-[10px] text-gray-400 whitespace-pre-wrap break-all overflow-x-auto border border-[var(--border-subtle)]">
                  {details.raw}
                </pre>
              </details>
            </div>
          ) : (
            <div className="text-gray-500 text-xs px-3 py-2">Loading...</div>
          )}
        </div>
      )}
    </div>
  );
}
